/** Shared PMS types — single source for web, mobile, API */

export const APP_NAME = "Startup Project Management";
export const APP_SLUG = "startup-project-management";
export const WEB_HOST = "projectmanagementstartups.pages.dev";
/** Store identifiers. Must never contain "linespotting" — see APPAR/CLAUDE.md. */
export const BUNDLE_ID_IOS = "org.startuppmanagement";
export const PACKAGE_ANDROID = "org.startuppmanagement";

/** IAP product IDs + live store price points */
export const IAP = {
  userYearly: "pms_user_yearly",
  adminYearly: "pms_admin_yearly",
  userPriceUsdYear: 8.99,
  adminPriceUsdYear: 18.99,
} as const;

export type WorkspaceRole = "owner" | "admin" | "member" | "viewer";
export type MemberStatus = "invited" | "active" | "disabled";
export type PlanEntitlement = "none" | "user_yearly" | "admin_yearly";

export type TaskStatus =
  | "backlog"
  | "todo"
  | "in_progress"
  | "in_review"
  | "done"
  | "cancelled";

export type Priority = "none" | "low" | "medium" | "high" | "urgent";

export type OrgLevelCode = "FOUNDERS" | "HEADS" | "LEADS" | "CONTRIBUTORS";

export const ORG_LEVELS: { code: OrgLevelCode; rank: number; label: string; subtitle: string }[] = [
  { code: "FOUNDERS", rank: 1, label: "Founders / Board", subtitle: "Sets direction" },
  { code: "HEADS", rank: 2, label: "Heads", subtitle: "Owns a function" },
  { code: "LEADS", rank: 3, label: "Leads", subtitle: "Coordinates a pod" },
  { code: "CONTRIBUTORS", rank: 4, label: "Contributors", subtitle: "Ships the work" },
];

export type ReportId =
  | "team_skills"
  | "org_pyramid"
  | "project_tasks"
  | "gantt_timeline"
  | "time_log"
  | "budget_runway"
  | "standup_weekly"
  | "workspace_snapshot";

export const REPORT_CATALOG: { id: ReportId; title: string }[] = [
  { id: "team_skills", title: "Team skills matrix" },
  { id: "org_pyramid", title: "Org pyramid snapshot" },
  { id: "project_tasks", title: "Project task list" },
  { id: "gantt_timeline", title: "Timeline / Gantt text" },
  { id: "time_log", title: "Time entries" },
  { id: "budget_runway", title: "Budget & runway" },
  { id: "standup_weekly", title: "Weekly standup pack" },
  { id: "workspace_snapshot", title: "Full workspace summary" },
];

export interface PersonSkillAxes {
  competence: number; // 0-10
  wantToDo: number;
  wantToLearn: number;
}

export function clampSkill(n: number): number {
  return Math.max(0, Math.min(10, Math.round(n)));
}

export function assignmentScore(s: PersonSkillAxes): number {
  return 0.55 * s.competence + 0.35 * s.wantToDo + 0.1 * s.wantToLearn;
}

export function gapFlag(s: PersonSkillAxes): boolean {
  return s.competence <= 4 && (s.wantToDo >= 7 || s.wantToLearn >= 7);
}

export type SkillSignal = "primary" | "training" | "avoid_overload" | "learning" | "risk" | "neutral";

export function skillSignal(s: PersonSkillAxes): SkillSignal {
  const { competence: c, wantToDo: d, wantToLearn: l } = s;
  if (c >= 7 && d >= 7) return "primary";
  if (c >= 7 && d <= 4) return "avoid_overload";
  if (c <= 4 && d >= 7 && l >= 7) return "training";
  if (c <= 4 && d >= 7 && l <= 4) return "risk";
  if (c <= 4 && d <= 4 && l >= 7) return "learning";
  return "neutral";
}

/** Budget runway months; null = cash generative / infinite */
export function runwayMonths(openingCash: number, monthlyNetBurn: number): number | null {
  if (monthlyNetBurn <= 0) return null;
  return openingCash / monthlyNetBurn;
}

export type RunwayStatus = "healthy" | "warning" | "critical" | "generative";

export function runwayStatus(
  months: number | null,
  warningAt = 6,
  criticalAt = 3
): RunwayStatus {
  if (months === null) return "generative";
  if (months < criticalAt) return "critical";
  if (months < warningAt) return "warning";
  return "healthy";
}

export function reportFilename(workspace: string, reportName: string, date = new Date()): string {
  const y = date.toISOString().slice(0, 10);
  const safeWs = workspace.replace(/[\\/:*?"<>|]/g, "-").trim() || "Workspace";
  const safeName = reportName.replace(/[\\/:*?"<>|]/g, "-").trim() || "Report";
  return `${y} - ${safeWs} - ${safeName}.txt`;
}

export function buildReportHeader(opts: {
  reportId: ReportId;
  workspace: string;
  generatedBy: string;
  extra?: Record<string, string>;
}): string {
  const lines = [
    "PMS_REPORT_FORMAT: 1.0",
    `REPORT_ID: ${opts.reportId}`,
    `WORKSPACE: ${opts.workspace}`,
    `GENERATED_AT: ${new Date().toISOString()}`,
    `GENERATED_BY: ${opts.generatedBy}`,
  ];
  if (opts.extra) {
    for (const [k, v] of Object.entries(opts.extra)) {
      lines.push(`${k}: ${v}`);
    }
  }
  lines.push("---");
  return lines.join("\n");
}

export const REPORT_FOOTER =
  "END_OF_REPORT\nConvert this file with Claude or Grok to Excel, PDF, or Word.";

/** Nav labels shared web + mobile */
export const NAV = {
  home: "Home",
  people: "People",
  projects: "Projects",
  budget: "Budget",
  reports: "Reports",
  more: "More",
  settings: "Settings",
  ai: "AI",
} as const;

/* ------------------------------------------------------------------ */
/* API payload shapes — shared by web, mobile and the Worker           */
/* ------------------------------------------------------------------ */

export interface ApiWorkspace {
  id: string;
  slug: string;
  name: string;
  plan: string;
  currency: string;
  counts: {
    people: number;
    projects: number;
    tasks: number;
    openTasks: number;
    vacancies: number;
  };
}

export interface ApiPersonSkill {
  skillId: string;
  name: string;
  domain: string | null;
  competence: number;
  wantToDo: number;
  wantToLearn: number;
  shareGrowth: boolean;
}

export interface ApiPerson {
  id: string;
  displayName: string;
  roleTitle: string | null;
  bio: string | null;
  isActive: boolean;
  skills: ApiPersonSkill[];
}

export interface ApiSeat {
  id: string;
  personId: string | null;
  personName: string | null;
  roleTitle: string | null;
  isVacancy: boolean;
  vacancyTitle: string | null;
}

export interface ApiPyramidLevel {
  id: string;
  levelIndex: number;
  levelCode: string;
  name: string;
  subtitle: string | null;
  seats: ApiSeat[];
}

export interface ApiProject {
  id: string;
  name: string;
  description: string | null;
  status: string;
  startDate: string | null;
  targetEndDate: string | null;
  ownerName: string | null;
  color: string | null;
  defaultView: string;
  taskCount: number;
  doneCount: number;
  percentComplete: number;
}

export interface ApiTask {
  id: string;
  projectId: string;
  projectName: string;
  parentTaskId: string | null;
  title: string;
  status: TaskStatus;
  priority: Priority;
  startAt: string | null;
  dueAt: string | null;
  isMilestone: boolean;
  estimateHours: number | null;
  percentComplete: number;
  assignees: { personId: string; name: string; role: string; isPrimary: boolean }[];
  dependsOn: string[];
}

export interface ApiBudgetLine {
  id: string;
  categoryCode: string;
  categoryLabel: string;
  planned: number;
  actual: number;
  variance: number;
  isRevenue: boolean;
  notes: string | null;
  projectId: string | null;
}

export interface ApiBudget {
  id: string;
  name: string;
  currency: string;
  periodStart: string;
  periodEnd: string;
  openingCash: number;
  plannedRevenue: number;
  plannedCost: number;
  actualRevenue: number;
  actualCost: number;
  netActual: number;
  closingCash: number;
  monthsInPeriod: number;
  monthlyBurn: number;
  runwayMonths: number | null;
  runwayStatus: RunwayStatus;
  lines: ApiBudgetLine[];
}

/** Months spanned by an inclusive ISO date range, minimum 1. */
export function monthsBetween(startIso: string, endIso: string): number {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const m =
    (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
  return Math.max(1, m);
}
